import { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Award, X, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CertificateProps {
  memberName: string;
  academyName: string;
  completionDate: string;
  onClose: () => void;
}

const Certificate = ({ memberName, academyName, completionDate, onClose }: CertificateProps) => {
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 100);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm print:bg-white print:backdrop-blur-none"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative max-w-4xl w-full print:max-w-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button - hidden when printing */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-muted-foreground hover:text-foreground transition-colors print:hidden"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Certificate */}
        <div
          id="certificate-content"
          className="relative w-full aspect-[1.414/1] bg-gradient-to-br from-charcoal via-background to-charcoal border-4 border-gold/40 rounded-lg overflow-hidden print:border-gold print:rounded-none"
        >
          {/* Decorative corners */}
          <div className="absolute top-4 left-4 w-24 h-24 border-l-2 border-t-2 border-gold/60" />
          <div className="absolute top-4 right-4 w-24 h-24 border-r-2 border-t-2 border-gold/60" />
          <div className="absolute bottom-4 left-4 w-24 h-24 border-l-2 border-b-2 border-gold/60" />
          <div className="absolute bottom-4 right-4 w-24 h-24 border-r-2 border-b-2 border-gold/60" />

          {/* Background pattern */}
          <div className="absolute inset-0 opacity-5">
            <div 
              className="absolute inset-0" 
              style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, #d4af37 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }} 
            />
          </div>

          {/* Content */}
          <div className="relative h-full flex flex-col items-center justify-center px-12 py-8 text-center">
            {/* Award icon */}
            <div className="mb-4">
              <Award className="w-16 h-16 text-gold" strokeWidth={1} />
            </div>

            {/* Header */}
            <h3 className="text-sm md:text-base uppercase tracking-[0.3em] text-gold/80 mb-2 print:text-gold">
              Certificate of Completion
            </h3>

            {/* Divider */}
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent mb-6 print:via-gold" />

            {/* Academy name */}
            <h1 className="text-2xl md:text-4xl font-light tracking-wider text-foreground mb-6 print:text-black">
              {academyName}
            </h1>

            {/* Presented to */}
            <p className="text-sm text-muted-foreground mb-2 print:text-gray-600">
              This is to certify that
            </p>

            {/* Member name */}
            <h2 className="text-xl md:text-3xl font-serif italic text-gold mb-4">
              {memberName}
            </h2>

            {/* Description */}
            <p className="text-sm text-muted-foreground max-w-md mb-8 print:text-gray-600">
              has successfully completed all modules and requirements of the above academy,
              demonstrating mastery of the skills and knowledge required.
            </p>

            {/* Date */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground print:text-gray-600">
              <span>Completed on</span>
              <span className="text-gold">{completionDate}</span>
            </div>

            {/* Signature area */}
            <div className="mt-8 flex items-center gap-12">
              <div className="text-center">
                <div className="w-32 h-px bg-gold/40 mb-2" />
                <span className="text-xs text-muted-foreground uppercase tracking-wider print:text-gray-600">
                  AURA Academy
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Print button - hidden when printing */}
        <div className="mt-6 flex justify-center print:hidden">
          <Button
            onClick={handlePrint}
            disabled={isPrinting}
            variant="cta"
            size="lg"
            className="gap-2"
          >
            <Printer className="w-5 h-5" />
            {isPrinting ? 'Preparing...' : 'Print / Save as PDF'}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Certificate;
